import {
  createAndAssignCustomPlanInputSchema,
  type CustomPlanListItemDTO,
} from '@form-factory/schema/contracts';
import { BillingInterval, Currency, Role } from '@form-factory/schema/enums';
import { convertCreditsToCurrency, formatUtils } from '@form-factory/shared';
import { Trans, useLingui } from '@lingui/react/macro';
import { createFileRoute } from '@tanstack/react-router';
import { Button, toast } from '@utima/ui';
import type { TypedFormState } from '@utima/ui-informed';
import { useState } from 'react';
import { z } from 'zod';

import { Alert } from '@/components/Alert';
import { Card } from '@/components/card/Card';
import { BasicForm } from '@/components/form/BasicForm';
import { SchemaFields } from '@/components/form/SchemaFields';
import { useFieldsSchema } from '@/components/form/useFieldsSchema';
import { FormPage } from '@/components/page/FormPage';
import { trpc } from '@/services/trpc';

// Form values type
type CreatePlanFormValues = Omit<
  z.infer<typeof createAndAssignCustomPlanInputSchema>,
  'organizationId'
>;

// Route definition
export const Route = createFileRoute(
  '/_baseLayout/organizations/$organizationId/plan-management',
)({
  component: OrganizationPlanManagementPage,
  beforeLoad: async ({ context: { accessGuard } }) =>
    accessGuard([Role.SuperAdmin]),
  loader: async ({ context: { trpcUtils }, params: { organizationId } }) =>
    trpcUtils.organizations.findOne.ensureData({ id: organizationId }),
});

// Page component
export function OrganizationPlanManagementPage() {
  const { organizationId } = Route.useParams();
  const { t } = useLingui();
  const utils = trpc.useUtils();
  const organizationData = Route.useLoaderData();
  const orgCurrency = organizationData.currency ?? Currency.USD; // Get org currency

  // --- Dynamic Schema Generation --- START ---
  const currencySymbol = orgCurrency === Currency.CZK ? 'Kč' : '$';

  const { schema: baseCreatePlanFormSchema } = useFieldsSchema(
    [
      {
        name: 'planName',
        label: t`Plan Name`,
        placeholder: t`Plan name`,
        required: true,
      },
      {
        name: 'planDescription',
        label: t`Description (Optional)`,
        type: 'text',
        placeholder: t`Plan description`,
        uiControl: 'textarea',
        defaultValue: 'Custom plan',
        uiProps: { rows: 3 },
        required: false,
      },
      {
        name: 'maxUsers',
        label: t`Max Users`,
        type: 'number',
        required: true,
        tooltip: t`Maximum number of users allowed.`,
        uiProps: { min: 0, step: 1 },
      },
      {
        name: 'maxDocuments',
        label: t`Max Documents`,
        type: 'number',
        required: true,
        tooltip: t`Maximum number of documents allowed.`,
        uiProps: { min: 0, step: 1 },
      },
      {
        name: 'monthlyBudget',
        label: t`Monthly Budget (${currencySymbol})`,
        type: 'number',
        required: true,
        tooltip: t`Included monthly usage allowance in ${orgCurrency.toUpperCase()}.`,
        uiProps: { min: 0, step: 0.01 },
      },
      {
        name: 'currency',
        label: t`Currency`,
        type: 'select',
        options: {
          [Currency.USD]: 'USD ($)',
          [Currency.CZK]: 'CZK (Kč)',
        },
        required: true,
        uiProps: { disabled: true },
        tooltip: t`Currency is locked to the organization's primary currency (${orgCurrency.toUpperCase()}).`,
      },
      {
        name: 'priceAmount',
        label: t`Price Amount (${currencySymbol})`,
        type: 'number',
        required: true,
        tooltip: t`Recurring cost per billing interval in ${orgCurrency.toUpperCase()}.`,
        uiProps: { min: 0.01, step: 0.01 },
      },
      {
        name: 'billingInterval',
        label: t`Billing Interval`,
        type: 'select',
        defaultValue: BillingInterval.Month,
        options: {
          [BillingInterval.Month]: t`Monthly`,
          [BillingInterval.Year]: t`Yearly`,
        },
        required: true,
      },
      {
        name: 'autoRenewal',
        label: t`Enable Auto-Renewal`,
        tooltip: t`Automatically adds the 'Renewal Amount' to your prepaid balance when it's too low to cover usage costs.`,
        type: 'checkbox',
        uiControl: 'switch',
        defaultValue: true,
        required: false,
      },
      {
        $relevant: {
          $when: ({ formState }) =>
            !!(formState?.values as CreatePlanFormValues)?.autoRenewal,
          $fields: [
            {
              name: 'autoRenewalThreshold',
              label: t`When balance goes below (${currencySymbol})`,
              type: 'number',
              tooltip: t`Balance level in ${orgCurrency.toUpperCase()} below which auto-renewal triggers. Required if auto-renewal enabled.`,
              uiProps: {
                min: convertCreditsToCurrency(5, orgCurrency),
                step: 0.01,
              },
              required: true,
            },
            {
              name: 'autoRenewalAmount',
              label: t`Add this amount to balance (${currencySymbol})`,
              type: 'number',
              tooltip: t`Amount in ${orgCurrency.toUpperCase()} to add to your balance when auto-renewal triggers. Required if auto-renewal enabled.`,
              uiProps: {
                min: convertCreditsToCurrency(5, orgCurrency),
                step: 0.01,
              },
              required: true,
            },
            {
              name: 'autoRenewalLimit',
              label: t`Monthly Limit (${currencySymbol}) (Optional)`,
              type: 'number',
              tooltip: t`Maximum total amount in ${orgCurrency.toUpperCase()} allowed for auto-renewals per month (0 or empty means no limit).`,
              uiProps: { min: 0, step: 0.01 },
              required: false,
            },
          ],
        },
      },
    ],
    [t, orgCurrency, currencySymbol],
  );
  // --- Dynamic Schema Generation --- END ---

  // --- Check if Org has Users ---
  const { data: hasUsers, isLoading: isLoadingHasUsers } =
    trpc.organizations.hasUsers.useQuery({ id: organizationId });

  // --- Fetch Custom Plans ---
  const {
    data: customPlans,
    isLoading: isLoadingPlans,
    error: plansError,
  } = trpc.subscriptions.listCustomEnterprisePlans.useQuery();

  // Filter plans by organization currency
  const filteredCustomPlans = customPlans?.filter(
    plan => plan.currency.toLowerCase() === orgCurrency.toLowerCase(),
  );

  // --- State for assigning existing plan ---
  const [assignPriceId, setAssignPriceId] = useState('');
  const [selectedPlanDetails, setSelectedPlanDetails] =
    useState<CustomPlanListItemDTO | null>(null);

  const assignPlanMutation = trpc.subscriptions.assignCustomPlan.useMutation({
    onSuccess: () => {
      toast.success(t`Existing custom plan assigned successfully.`);
      setAssignPriceId('');
      setSelectedPlanDetails(null);
      utils.subscriptions.getActive.invalidate({ organizationId });
      utils.organizations.findOne.invalidate({ id: organizationId });
    },
    onError: (error: { message: string }) => {
      toast.error(t`Failed to assign existing custom plan: ${error.message}`);
    },
  });

  const handleExistingPlanSelectChange = (
    event: React.ChangeEvent<HTMLSelectElement>,
  ) => {
    const selectedId = event.target.value;
    setAssignPriceId(selectedId);

    if (selectedId && customPlans) {
      const plan = customPlans.find(p => p.priceId === selectedId);
      setSelectedPlanDetails(plan || null);

      // Add check here: ensure selected plan matches org currency
      if (plan && plan.currency.toLowerCase() !== orgCurrency.toLowerCase()) {
        toast.warning(
          t`Selected plan currency (${plan.currency.toUpperCase()}) does not match organization currency (${orgCurrency.toUpperCase()}). Assignment disabled.`,
        );
        // Optionally disable the assign button or clear selection
      }
    } else {
      setSelectedPlanDetails(null);
    }
  };

  const handleAssignExistingPlan = () => {
    // Add check for empty selection
    if (!assignPriceId) {
      toast.warning(t`Please select a plan to assign.`);

      return;
    }

    // Replace brittle startsWith check with check against fetched plans
    if (!customPlans?.some(p => p.priceId === assignPriceId)) {
      toast.warning(t`Invalid plan selected. Please refresh and try again.`);

      return;
    }

    // Add explicit currency check before mutation
    if (
      selectedPlanDetails?.currency.toLowerCase() !== orgCurrency.toLowerCase()
    ) {
      toast.error(
        t`Cannot assign plan: Currency mismatch. Org is ${orgCurrency.toUpperCase()}, Plan is ${selectedPlanDetails?.currency.toUpperCase()}.`,
      );

      return;
    }

    assignPlanMutation.mutate({
      organizationId,
      stripePriceId: assignPriceId,
    });
  };

  // Mutation for creating and assigning new plan
  const createPlanMutation =
    trpc.subscriptions.createAndAssignCustomPlan.useMutation({
      onSuccess: () => {
        toast.success(t`New custom plan created and assigned successfully.`);
        // TODO: Implement form reset if possible with BasicForm/informed
        utils.subscriptions.getActive.invalidate({ organizationId });
        utils.organizations.findOne.invalidate({ id: organizationId });
      },
      onError: (error: unknown) => {
        console.error(
          'Error during createAndAssignCustomPlan mutation:',
          error,
        );

        // Simplified error handling
        const message =
          error instanceof Error
            ? error.message
            : t`An unknown error occurred.`;

        toast.error(t`Failed to create and assign plan:`, {
          description: message,
        });
      },
    });

  // Define initial form values including the locked currency
  const initialFormValues: CreatePlanFormValues = {
    planName: '',
    planDescription: t`Custom plan`,
    maxUsers: 10, // Or other sensible defaults
    maxDocuments: 100,
    monthlyBudget: convertCreditsToCurrency(5, orgCurrency),
    currency: orgCurrency, // Set initial currency here
    priceAmount: convertCreditsToCurrency(5, orgCurrency),
    billingInterval: BillingInterval.Month,
    autoRenewal: true,
    autoRenewalAmount: convertCreditsToCurrency(5, orgCurrency),
    autoRenewalLimit: null,
    autoRenewalThreshold: convertCreditsToCurrency(5, orgCurrency),
  };

  // Submit handler
  const handleCreateAndAssignPlan = (
    formState: TypedFormState<CreatePlanFormValues>,
  ) => {
    const values = formState.values;
    try {
      // Frontend validation against the base schema (currency specific mins are checked on backend)
      const validatedValues = createAndAssignCustomPlanInputSchema
        .omit({ organizationId: true })
        .parse(values);

      // Add specific frontend check for min values based on selected currency
      const selectedCurrency = validatedValues.currency || Currency.USD;
      const minAmountValidation = convertCreditsToCurrency(5, selectedCurrency);
      const minPriceValidation = selectedCurrency === Currency.CZK ? 1 : 0.01;

      if (validatedValues.priceAmount < minPriceValidation) {
        toast.error(
          t`Price Amount must be at least ${minPriceValidation}${selectedCurrency === Currency.CZK ? 'Kč' : '$'}.`,
        );

        return;
      }
      if (validatedValues.autoRenewal) {
        if (
          validatedValues.autoRenewalThreshold === undefined ||
          validatedValues.autoRenewalThreshold === null ||
          validatedValues.autoRenewalThreshold < minAmountValidation
        ) {
          toast.error(
            t`Renewal Threshold must be at least ${minAmountValidation}${selectedCurrency === Currency.CZK ? 'Kč' : '$'}.`,
          );

          return;
        }
        if (
          validatedValues.autoRenewalAmount === undefined ||
          validatedValues.autoRenewalAmount === null ||
          validatedValues.autoRenewalAmount < minAmountValidation
        ) {
          toast.error(
            t`Renewal Amount must be at least ${minAmountValidation}${selectedCurrency === Currency.CZK ? 'Kč' : '$'}.`,
          );

          return;
        }
      }

      createPlanMutation.mutate({
        ...validatedValues,
        organizationId,
      });
    } catch (error) {
      if (error instanceof z.ZodError) {
        // Improve error message
        const fieldErrors = error.errors.map(e => e.path.join('.')).join(', ');
        console.error('Form validation failed:', error.errors);
        toast.error(t`Validation failed for fields: ${fieldErrors}`, {
          description: t`Please check the highlighted fields (if available) or ensure all required fields are filled correctly.`,
        });
      } else {
        console.error('Error submitting form:', error);
        toast.error(t`An unexpected error occurred.`);
      }
    }
  };

  return (
    <FormPage
      title={`${t`Custom Plan`} - ${organizationData.name}`}
      breadcrumbs={[
        {
          label: t`Organizations`,
          to: '/organizations',
        },
        {
          label: organizationData.name,
          to: '/organizations/$organizationId/edit',
          params: { organizationId },
        },
        {
          label: t`Custom Plan`,
          to: '/organizations/$organizationId/plan-management',
          params: { organizationId },
        },
      ]}
    >
      {/* Use the local Alert component with description and actions props */}
      {!isLoadingHasUsers &&
        hasUsers === false &&
        !organizationData.stripeCustomerId && (
          <Alert
            variant='warning'
            title={<Trans>Organization Has No Users</Trans>}
            className='mb-6'
            description={
              <Trans>
                Assigning or creating a plan requires a Stripe Customer ID. If
                one does not exist, it must be created using an email address
                from a user in this organization. Please add a user (e.g., an
                Administrator) to this organization before assigning a plan.
              </Trans>
            }
          />
        )}

      <Card title='Organization stripe details' className='mb-4'>
        <p>
          <Trans>Stripe Customer ID: {organizationData.stripeCustomerId}</Trans>
        </p>
      </Card>

      <Alert
        variant='danger'
        title={<Trans>DANGER</Trans>}
        className='mb-6'
        description={
          <div>
            <b>
              <Trans>
                You are about to assign a custom plan for FORM FACTORY. This is
                will cancel their current plan and assign a new one.
              </Trans>
            </b>
            <br />
            <b>
              <Trans>Proceed at your own risk.</Trans>
            </b>
          </div>
        }
      />

      {/* UI Sections are not wrapped in Protected as the Route handles it */}
      <div className='grid grid-cols-1 gap-6 md:grid-cols-2'>
        {/* Section 1: Assign Existing Plan */}
        <Card title='Assign Existing Custom Plan' collapsible={false}>
          <div className='space-y-4'>
            <p className='text-sm text-gray-500'>
              <Trans>
                Select an existing custom Enterprise plan configured in Stripe.
              </Trans>
            </p>
            {/* Loading/Error State for Dropdown */}
            {isLoadingPlans && (
              <p>
                <Trans>Loading plans...</Trans>
              </p>
            )}
            {plansError && (
              <p className='text-destructive'>
                <Trans>Error loading plans:</Trans> {plansError.message}
              </p>
            )}

            {/* Dropdown and Button Area */}
            {!isLoadingPlans && !plansError && (
              <div className='space-y-4'>
                <div className='flex items-center gap-2'>
                  <select
                    id='assign-plan-select'
                    value={assignPriceId}
                    onChange={handleExistingPlanSelectChange}
                    disabled={
                      assignPlanMutation.isPending ||
                      isLoadingPlans ||
                      isLoadingHasUsers ||
                      !hasUsers ||
                      !customPlans ||
                      customPlans.length === 0
                    }
                    required
                    className='flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50'
                  >
                    <option value='' disabled={assignPriceId !== ''}>
                      {customPlans && customPlans.length > 0
                        ? t`-- Select a Plan --`
                        : t`-- No Custom Plans Found --`}
                    </option>
                    {filteredCustomPlans?.map(plan => (
                      <option key={plan.priceId} value={plan.priceId}>
                        {`${plan.productName} - (${formatUtils.currency(plan.amount, plan.currency)} / ${plan.interval === BillingInterval.Month ? 'mo' : 'yr'})`}
                      </option>
                    ))}
                  </select>
                  <Button
                    data-cta='admin-organizations-plan-management-assign-existing-button'
                    onClick={handleAssignExistingPlan}
                    disabled={
                      !assignPriceId ||
                      assignPlanMutation.isPending ||
                      isLoadingPlans ||
                      isLoadingHasUsers ||
                      !hasUsers
                    }
                    loading={assignPlanMutation.isPending}
                  >
                    <Trans>Assign Existing</Trans>
                  </Button>
                </div>

                {/* Conditionally Display Selected Plan Details - WITH LIMITS */}
                {selectedPlanDetails && (
                  <div className='mt-3 space-y-1 rounded-md border border-dashed border-border bg-background/50 p-3 text-sm'>
                    <p>
                      <span className='font-medium'>
                        <Trans>Selected:</Trans>
                      </span>{' '}
                      {selectedPlanDetails.productName}
                    </p>
                    <p>
                      <span className='font-medium'>
                        <Trans>Price:</Trans>
                      </span>{' '}
                      {formatUtils.currency(
                        selectedPlanDetails.amount,
                        selectedPlanDetails.currency as Currency,
                      )}{' '}
                      /{' '}
                      {selectedPlanDetails.interval === BillingInterval.Month
                        ? t`month`
                        : t`year`}
                    </p>
                    {/* Display limits */}
                    {selectedPlanDetails.maxUsers !== undefined && (
                      <p>
                        <span className='font-medium'>
                          <Trans>Max Users:</Trans>
                        </span>{' '}
                        {selectedPlanDetails.maxUsers === 0
                          ? t`Unlimited`
                          : selectedPlanDetails.maxUsers}
                      </p>
                    )}
                    {selectedPlanDetails.maxDocuments !== undefined && (
                      <p>
                        <span className='font-medium'>
                          <Trans>Max Docs:</Trans>
                        </span>{' '}
                        {selectedPlanDetails.maxDocuments === 0
                          ? t`Unlimited`
                          : selectedPlanDetails.maxDocuments}
                      </p>
                    )}
                    {selectedPlanDetails.monthlyBudget !== undefined && (
                      <p>
                        <span className='font-medium'>
                          <Trans>Monthly Budget:</Trans>
                        </span>{' '}
                        {formatUtils.currency(
                          convertCreditsToCurrency(
                            selectedPlanDetails.monthlyBudget,
                            selectedPlanDetails.currency as Currency,
                          ),
                          selectedPlanDetails.currency as Currency,
                        )}
                      </p>
                    )}
                    {/* Display Auto-Renewal Settings from Metadata */}
                    {selectedPlanDetails.metadata && (
                      <>
                        <p>
                          <span className='font-medium'>
                            <Trans>Auto-Renewal:</Trans>
                          </span>{' '}
                          {selectedPlanDetails.metadata.autoRenewal === 'true'
                            ? t`Enabled`
                            : t`Disabled`}
                        </p>
                        {selectedPlanDetails.metadata.autoRenewal ===
                          'true' && (
                          <>
                            {selectedPlanDetails.metadata.autoRenewalAmount && (
                              <p>
                                <span className='font-medium'>
                                  <Trans>Renewal Amount:</Trans>
                                </span>{' '}
                                {formatUtils.currency(
                                  convertCreditsToCurrency(
                                    Number(
                                      selectedPlanDetails.metadata
                                        .autoRenewalAmount,
                                    ),
                                    selectedPlanDetails.currency as Currency,
                                  ),
                                  selectedPlanDetails.currency as Currency,
                                )}
                              </p>
                            )}
                            {selectedPlanDetails.metadata
                              .autoRenewalThreshold && (
                              <p>
                                <span className='font-medium'>
                                  <Trans>Renewal Threshold:</Trans>
                                </span>{' '}
                                {formatUtils.currency(
                                  convertCreditsToCurrency(
                                    Number(
                                      selectedPlanDetails.metadata
                                        .autoRenewalThreshold,
                                    ),
                                    selectedPlanDetails.currency as Currency,
                                  ),
                                  selectedPlanDetails.currency as Currency,
                                )}
                              </p>
                            )}
                            {selectedPlanDetails.metadata.autoRenewalLimit &&
                              Number(
                                selectedPlanDetails.metadata.autoRenewalLimit,
                              ) > 0 && (
                                <p>
                                  <span className='font-medium'>
                                    <Trans>Monthly Limit:</Trans>
                                  </span>{' '}
                                  {formatUtils.currency(
                                    convertCreditsToCurrency(
                                      Number(
                                        selectedPlanDetails.metadata
                                          .autoRenewalLimit,
                                      ),
                                      selectedPlanDetails.currency as Currency,
                                    ),
                                    selectedPlanDetails.currency as Currency,
                                  )}
                                </p>
                              )}
                            {(!selectedPlanDetails.metadata.autoRenewalLimit ||
                              Number(
                                selectedPlanDetails.metadata.autoRenewalLimit,
                              ) === 0) && (
                              <p>
                                <span className='font-medium'>
                                  <Trans>Monthly Limit:</Trans>
                                </span>{' '}
                                {t`None`}
                              </p>
                            )}
                          </>
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </Card>

        {/* Section 2: Create and Assign New Plan */}
        <Card title='Create & Assign New Custom Plan' collapsible={false}>
          <BasicForm<CreatePlanFormValues>
            onSubmit={handleCreateAndAssignPlan}
            initialValues={initialFormValues}
          >
            <SchemaFields schema={baseCreatePlanFormSchema} />
            <div className='mt-6 flex justify-end'>
              <Button
                data-cta='admin-organizations-plan-management-create-and-assign-button'
                type='submit'
                loading={createPlanMutation.isPending}
                disabled={isLoadingHasUsers || !hasUsers}
                title={
                  isLoadingHasUsers || !hasUsers
                    ? t`Requires at least one user in the organization.`
                    : undefined
                }
              >
                <Trans>Create & Assign</Trans>
              </Button>
            </div>
          </BasicForm>
        </Card>
      </div>
    </FormPage>
  );
}
